import { getOrgIdFromToken, getUserIdFromToken } from "@/api/utils";
import { QUERY_CACHE_GC_TIME_MS, queryClient } from "@/queryClient";
import { useAuth0 } from "@auth0/auth0-react";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { defaultShouldDehydrateQuery, type Query } from "@tanstack/react-query";
import {
  type PersistedClient,
  type PersistQueryClientOptions,
} from "@tanstack/react-query-persist-client";
import { useEffect, useState, type ReactNode } from "react";

export const QUERY_CACHE_STORAGE_KEY = "COHESIVE_QUERY_CACHE";
export const QUERY_CACHE_IDENTITY_KEY = "COHESIVE_QUERY_CACHE_IDENTITY";

/**
 * Bump this whenever the shape of persisted query data changes in a way that
 * would break consumers of restored caches (e.g. renamed query keys, changed
 * API response envelopes, redaction rules). Any mismatch throws the whole
 * persisted cache away.
 */
// v6 removes field-level presigned-URL redaction (and its `_persistRedacted`
// markers) entirely. Redaction only governed writes, so without a bump a v5
// snapshot would keep hydrating redacted, marker-carrying rows nothing
// recognizes anymore.
// v7: workspace/canvas/file responses gained capability fields; persisted
// pre-capability caches read as "allowed" and must not survive the upgrade.
// v8 strips presigned URLs on restore. Snapshots written under v6/v7 hold
// hour-old presigns that CloudFront answers with 403; discard them rather
// than wait for the first restore to strip them.
const QUERY_CACHE_BUSTER = "v8";

/**
 * Query-key segments whose data must never be written to localStorage:
 * short-lived credentials (TipTap/collab JWTs), fetched file bodies,
 * Stripe/billing payloads, and `/me`.
 *
 * `/me` is session state the router reads, not view data: `App` sends the user
 * to create-organization on `organizations: []` and blocks rendering while it
 * loads. Restoring it means a snapshot taken seconds earlier — before signup
 * finished joining an org, before a plan changed, before permissions were
 * granted — decides routing for the new session. There is nothing to gain
 * either; the app renders nothing until `/me` resolves regardless.
 */
const NEVER_PERSIST_KEY_SEGMENTS = new Set<string>([
  "tiptapToken",
  "collaborationToken",
  "fileRecordText",
  "setupIntentClientSecret",
  "paymentMethods",
  "subscription",
  "stripeInvoices",
  "creditUsagePerUser",
  "userInfo",
  // Access answers must come from a live check every session: the
  // ["permission", ...] fan-out entries carry gcTime Infinity with no queryFn
  // and no observer, so a restored copy of a since-revoked permission would
  // never correct itself.
  "permission",
  "checkBulkPermissions",
]);

/**
 * Whether a query is safe to persist to localStorage. Sensitive/ephemeral
 * keys are denylisted, and individual queries can opt out with
 * `meta: { persist: false }`.
 *
 * Queries that opt out are also the ones allowed to set a `gcTime` shorter
 * than `maxAge` — see the invariant documented on `QUERY_CACHE_GC_TIME_MS`.
 */
export function shouldPersistQuery(
  query: Pick<Query, "queryKey" | "meta">,
): boolean {
  if (query.meta?.persist === false) {
    return false;
  }
  return !query.queryKey.some(
    (segment) =>
      typeof segment === "string" && NEVER_PERSIST_KEY_SEGMENTS.has(segment),
  );
}

/**
 * Presigned URL / upload credential fields, dropped from every payload on
 * its way to and from disk, and from any live row whose presigns have run
 * out.
 *
 * Presigns die after an hour (`DefaultExpires` in the backend's CloudFront
 * signer) and the snapshot lives for `maxAge` — a day — so any URL coming off
 * disk is assumed dead. Consumers that render one get a 403 from CloudFront
 * and, having no reason to expect a signed URL to fail, never retry. An
 * in-memory row is dead the same way once an hour has passed without a
 * refetch, which is why `dropPresignedFields` is exported.
 *
 * Matched by field name at any depth, because the same payloads travel under
 * many keys: file records appear under `fileRecord(s)` but also
 * `["archived", "files"]`, search results and the workspace tree.
 *
 * `url` is deliberately included even though it is a generic name — in this
 * API it is almost always an S3 presign (file records, skill files, message
 * attachments). The rare plain link caught by it (web-search citations) is
 * restored by the refetch stripping already forces.
 */
const PRESIGNED_FIELDS = new Set<string>([
  "url",
  "download_url",
  "preview_urls",
  "upload_url",
  "upload_field_values",
  "hero_image_url",
  "hero_image_light_url",
  "hero_image_dark_url",
]);

/**
 * Recursively drop presign fields from a value, reporting whether any were
 * removed. Used on restore here, and on expired live rows by
 * `useHydrateFileRecordEntity`.
 */
export function dropPresignedFields(data: unknown): {
  data: unknown;
  stripped: boolean;
} {
  if (Array.isArray(data)) {
    let stripped = false;
    const next = data.map((item) => {
      const result = dropPresignedFields(item);
      stripped = stripped || result.stripped;
      return result.data;
    });
    return { data: next, stripped };
  }
  if (data && typeof data === "object") {
    let stripped = false;
    const next: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(
      data as Record<string, unknown>,
    )) {
      if (PRESIGNED_FIELDS.has(key)) {
        stripped = true;
        continue;
      }
      const result = dropPresignedFields(value);
      stripped = stripped || result.stripped;
      next[key] = result.data;
    }
    return { data: next, stripped };
  }
  return { data, stripped: false };
}

/**
 * Strip presigned URLs out of a snapshot, on the way to disk and back.
 *
 * On write because a presign is dead long before the snapshot is, so writing
 * it is pure cost (a third of the snapshot on a canvas-heavy session); on
 * restore because snapshots written by older builds still carry them. Only
 * the dehydrated copy is touched, so the live cache — which holds URLs that
 * actually work — stays as it is for the whole session and no consumer ever
 * has to tell a redacted row from a real one.
 *
 * A stripped payload can no longer render media, so it must not restore as
 * *fresh*, or a reload would leave thumbnails and previews blank with nothing
 * scheduled to heal them. `isInvalidated` makes the restored query stale
 * regardless of `staleTime`, so React Query refetches as soon as an observer
 * mounts while still serving the cached metadata (path, size, status, tree
 * structure) immediately.
 *
 * Only queries that actually lost a field are marked, so the ones carrying no
 * presigned data still restore fresh and keep the instant-load win. That is
 * why marking cannot be the only thing that settles a restored row: a payload
 * the server never gave a `url` — its placeholder for a dead reference — is
 * indistinguishable here from one that simply carries no media, and neither
 * loses a field. Consumers that need a settled answer ask for one themselves
 * (see `isRestoredQueryData`).
 *
 * `dataUpdatedAt` is deliberately left untouched: `serializePersistedClient`
 * keeps the newest queries by it when the snapshot is over budget, and
 * zeroing it would make file-record entries the first casualties every time.
 */
export function stripPersistedClient(client: PersistedClient): PersistedClient {
  return {
    ...client,
    clientState: {
      ...client.clientState,
      queries: client.clientState.queries.map((query) => {
        const { data, stripped } = dropPresignedFields(query.state.data);
        if (!stripped) return query;
        return {
          ...query,
          state: { ...query.state, data, isInvalidated: true },
        };
      }),
    },
  };
}

/** When the snapshot was read back in; 0 until one is. */
let restoredAtMs = 0;

/**
 * Whether query data of this age came off disk instead of the network:
 * everything the snapshot carried predates the restore, everything fetched
 * (or `setQueryData`-ed from a server response) since does not.
 *
 * Restored file-record rows have lost their presigned URLs, which leaves them
 * indistinguishable on shape from a server payload that genuinely has none —
 * a dead reference. Callers that read meaning into a missing `url` have to
 * wait for a fresh answer.
 *
 * Restore is not the only source of an untrustworthy presign: an in-memory
 * row the session has held for over an hour carries URLs CloudFront now
 * 403s. `useHydrateFileRecordEntity` reads the expiry out of the URL and,
 * for a node mounting on such a row, treats it exactly like a restored one.
 *
 * Stripping marks the rows it touched `isInvalidated`, so those refetch on
 * mount; a row that lost nothing would otherwise sit unread for its whole
 * `staleTime`, which is why `useFileRecord` reads this too and counts any
 * restored row as stale. Deliberately generic: the persister knows about
 * presign fields, not about what any payload shape means.
 */
export function isRestoredQueryData(dataUpdatedAt: number): boolean {
  return restoredAtMs > 0 && dataUpdatedAt < restoredAtMs;
}

function readPersistedClient(cached: string): PersistedClient {
  restoredAtMs = Date.now();
  return stripPersistedClient(JSON.parse(cached) as PersistedClient);
}

/**
 * Characters one snapshot may occupy in localStorage.
 *
 * Measured as the largest value one `setItem` accepts on a blank origin, in
 * Playwright's Chromium 140, Firefox 141 and WebKit 26 builds. Chromium and
 * Firefox take 5,242,879 UTF-16 code units whatever the value holds. WebKit
 * (Safari, and the iOS app's web view) counts bytes against 5 MiB and stores
 * a Latin-1 string one byte per character (`StorageMap::setItem`,
 * `sizeInBytes`): the same 5.24 M for pure Latin-1, but 2,621,439 code units
 * once a single character outside Latin-1 is in the value, which one file
 * name with a curly quote or an emoji puts in the snapshot. The app's other
 * keys (the Auth0 cache and the identity marker) take under 10 K. 2 M fits
 * that smallest case with 600 K to spare. Measured after a 300-file canvas
 * it holds that canvas's rows next to the 1.2 M-character integrations
 * query, which refetches on every mount and so always ranks newest; the
 * canvas list and, after a few canvases, the workspace tree fall out.
 *
 * ponytail: 2 M characters is the ceiling of a localStorage-backed cache;
 * past it, restore coverage needs IndexedDB (an async persister with no
 * per-origin character budget), not a bigger number here.
 */
export const PERSISTED_SNAPSHOT_BUDGET_CHARS = 2_000_000;

/**
 * The snapshot as written: presigns stripped, then queries kept newest
 * `dataUpdatedAt` first until the budget is spent. Each query is serialized
 * once and an older one that would overflow is left out of this write, so a
 * snapshot over budget still lands in a single `setItem`. The library's
 * alternative — evict one query on the quota error and re-serialize the rest,
 * in a loop — blocked the main thread for seconds per write once three large
 * canvases had been visited.
 */
export function serializePersistedClient(
  client: PersistedClient,
  budget = PERSISTED_SNAPSHOT_BUDGET_CHARS,
): string {
  const stripped = stripPersistedClient(client);
  const envelope = JSON.stringify({
    ...stripped,
    clientState: { ...stripped.clientState, queries: [] },
  });
  const queries = [...stripped.clientState.queries].sort(
    (a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt,
  );
  const parts: string[] = [];
  let length = envelope.length;
  for (const query of queries) {
    const json = JSON.stringify(query);
    if (length + json.length + 1 > budget) continue;
    parts.push(json);
    length += json.length + 1;
  }
  // The envelope ends in `"queries":[]}}`; the kept queries go inside it.
  const open = envelope.lastIndexOf("[]") + 1;
  return envelope.slice(0, open) + parts.join(",") + envelope.slice(open);
}

const persister = createSyncStoragePersister({
  storage: typeof window === "undefined" ? undefined : window.localStorage,
  key: QUERY_CACHE_STORAGE_KEY,
  serialize: serializePersistedClient,
  deserialize: readPersistedClient,
  // No `retry`: a write that still fails (another key filled the origin's
  // quota, private mode) is skipped and the previous snapshot stays, instead
  // of the evict-and-retry loop the budget above makes unnecessary.
});

export const persistOptions: Omit<PersistQueryClientOptions, "queryClient"> = {
  persister,
  // Keep in lockstep with `QUERY_CACHE_GC_TIME_MS` — see queryClient.ts.
  maxAge: QUERY_CACHE_GC_TIME_MS,
  buster: QUERY_CACHE_BUSTER,
  dehydrateOptions: {
    shouldDehydrateQuery: (query) =>
      defaultShouldDehydrateQuery(query) && shouldPersistQuery(query),
    // Mutations have no registered defaults to resume from after a reload,
    // so persisting them would only produce dead entries.
    shouldDehydrateMutation: () => false,
  },
};

/** Drops the persisted query cache and its identity marker from localStorage. */
export function clearPersistedQueryCache(): void {
  localStorage.removeItem(QUERY_CACHE_STORAGE_KEY);
  localStorage.removeItem(QUERY_CACHE_IDENTITY_KEY);
}

/**
 * Wipe both the in-memory and persisted React Query caches. Call this before
 * every Auth0 `logout()` so the next browser session cannot restore this
 * user's data.
 */
export function clearSessionQueryCaches(): void {
  clearPersistedQueryCache();
  queryClient.clear();
}

/** Recorded for a token whose `c_org_id` claim is absent. */
const NO_ORG_IDENTITY = "orgless";

/**
 * Recorded when the identity check itself failed, so the cache the session
 * goes on to write has no verified owner.
 *
 * Real identities are always `orgId:userId`, so this can never match one and
 * the next check is guaranteed to wipe. Leaving the marker absent instead
 * would read as a fresh login on the next load, handing whatever the
 * unverified session persisted to the next user of the browser.
 */
const UNVERIFIED_IDENTITY = "unverified";

/**
 * Owner of the cache for `token`, as `orgId:userId`.
 *
 * `getOrgIdFromToken` throws when the token carries no `c_org_id`, which is
 * the normal state between signup and joining an org. That is a real identity,
 * not a failure, and it has to be recorded: leaving the marker unwritten makes
 * the org-scoped re-auth after create-organization look like a fresh login, so
 * the org-less snapshot survives and `/me` restores with `organizations: []` —
 * bouncing the user straight back to the create-organization page.
 */
function cacheIdentity(token: string): string {
  let orgId: string;
  try {
    orgId = getOrgIdFromToken(token);
  } catch {
    orgId = NO_ORG_IDENTITY;
  }
  return `${orgId}:${getUserIdFromToken(token)}`;
}

/**
 * The persisted cache is restored before Auth0 resolves, so it may belong to
 * a different user/org (shared browser, org switch). Children are gated until
 * the token's `orgId:userId` is checked against the identity recorded with
 * the cache — mirroring `OpenTabsContext` — so restored query data is never
 * shown for the wrong account.
 */
export function QueryCacheIdentityGuard({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [identityReady, setIdentityReady] = useState(false);

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      setIdentityReady(true);
      return;
    }

    let cancelled = false;
    setIdentityReady(false);
    getAccessTokenSilently()
      .then((token) => {
        if (cancelled) return;
        const identity = cacheIdentity(token);
        const stored = localStorage.getItem(QUERY_CACHE_IDENTITY_KEY);
        // Only wipe when a *different* identity owned the cache. A missing
        // marker (fresh login after logout) must not clear queries that
        // already fetched between auth resolving and this check finishing.
        if (stored !== null && stored !== identity) {
          clearPersistedQueryCache();
          queryClient.clear();
        }
        localStorage.setItem(QUERY_CACHE_IDENTITY_KEY, identity);
        setIdentityReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        // Failed identity check — don't risk showing another user's data.
        clearPersistedQueryCache();
        queryClient.clear();
        localStorage.setItem(QUERY_CACHE_IDENTITY_KEY, UNVERIFIED_IDENTITY);
        setIdentityReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  if (isLoading || (isAuthenticated && !identityReady)) {
    return null;
  }

  return children;
}
